function encodePayload(payload) {
  const json = JSON.stringify(payload);
  if (typeof btoa === 'function') return btoa(unescape(encodeURIComponent(json)));
  return Buffer.from(json, 'utf8').toString('base64');
}

function decodePayload(encoded) {
  if (typeof atob === 'function') return JSON.parse(decodeURIComponent(escape(atob(encoded))));
  return JSON.parse(Buffer.from(encoded, 'base64').toString('utf8'));
}

function shortcodeArg(value) {
  return JSON.stringify(String(value ?? ''));
}

const DEFAULT_IMAGE_SIZES = '(max-width: 600px) 400px, 850px';
const IMAGE_HOVER_PRESETS = new Set(['scale', 'lift', 'rotate', 'shimmer', 'color-shift']);
const IMAGE_EFFECT_PRESETS = new Set(['dim', 'darken', 'grayscale', 'warm', 'cool']);

function normalizeKey(value) {
  if (!value) return '';
  return String(value).trim();
}

function addEntryAlias(index, key, entry) {
  const normalized = normalizeKey(key);
  if (!normalized || index.has(normalized)) return;
  index.set(normalized, entry);
}

export function buildImageEntryIndex(resolvedMap) {
  const index = new Map();
  for (const [originalUrl, entry] of resolvedMap || []) {
    addEntryAlias(index, originalUrl, entry);
    addEntryAlias(index, entry?.htmlPath, entry);
    addEntryAlias(index, entry?.localPath, entry);
    addEntryAlias(index, entry?.shortcodeSrc, entry);
    if (entry?.htmlPath?.startsWith('/')) addEntryAlias(index, entry.htmlPath.slice(1), entry);
    if (entry?.localPath?.startsWith('src/')) addEntryAlias(index, entry.localPath.slice(4), entry);
  }
  return index;
}

function firstSrcsetUrl(value) {
  return (
    String(value || '')
      .split(',')[0]
      ?.trim()
      .split(/\s+/)[0] || ''
  );
}

function imageExtension(src) {
  const clean = String(src || '')
    .split(/[?#]/)[0]
    .toLowerCase();
  const match = clean.match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
}

/**
 * Is this <img> a logo?
 *
 * Exported because C5's LCP selection needs the SAME answer this module uses to
 * decide what to optimize. A second copy of this selector list would let the
 * two disagree about a nav logo, and the disagreement would be a page with two
 * high-priority images — which is the same as having none.
 */
export function isLogoImage(img) {
  return isInsideLogo(img) || /\b(cs-logo|favicon)\b/i.test(img.getAttribute('class') || '');
}

/** The inverse of `shouldSkipImage`: an image worth optimizing and preloading. */
export function isOptimizableImage(img) {
  return !shouldSkipImage(img);
}

function isInsideLogo(img) {
  return !!img.closest('.logo-slot, .logo, .cs-logo, .footer-logo, [data-pb-editable="logo"]');
}

function shouldSkipImage(img) {
  const src = img.getAttribute('src') || firstSrcsetUrl(img.getAttribute('srcset'));
  const ext = imageExtension(src);
  const cls = img.getAttribute('class') || '';
  const width = Number.parseInt(img.getAttribute('width') || '', 10);
  return (
    !src ||
    img.closest('noscript') ||
    src.startsWith('data:') ||
    ext === 'svg' ||
    ext === 'gif' ||
    (Number.isFinite(width) && width > 0 && width < 100) ||
    /\b(cs-logo|favicon)\b/i.test(cls) ||
    isInsideLogo(img)
  );
}

export function resolveImageEntry(img, index) {
  const candidates = [img.getAttribute('src'), firstSrcsetUrl(img.getAttribute('srcset'))];
  for (const candidate of candidates) {
    const entry = index.get(normalizeKey(candidate));
    if (entry?.shortcodeSrc) return entry;
  }
  return null;
}

/**
 * @param {Element} img the `<img>` supplying src/alt/loading
 * @param {object} entry resolved asset entry
 * @param {Element|null} [picture] the `<picture>` being REPLACED, when there is one
 *
 * The shortcode emits `<picture class="${className}">…<img></picture>`, so `className`
 * is the PICTURE's slot. Replacing a `<picture class="cs-background">` while sending the
 * inner img's class dropped the wrapper's class on the floor — 638 of the 712 convertible
 * `<picture>` blocks in the section corpus carry one, 44 of them `cs-background`, whose
 * rules are the full-bleed `position:absolute; z-index:-2` and the `::before` scrim. Losing
 * it is what put uicompass's white hero title on a white background with nothing behind it.
 * The Astro rewriter has always carried the wrapper class (`astro-image-rewriter.js:172`);
 * this is the 11ty target catching up, and `imgClass` is the second slot it needs to do so
 * without trading one silent loss for another.
 *
 * The bare-`<img>` path deliberately keeps sending the img's class as `className`, i.e.
 * onto the generated `<picture>`. That placement is arguable, but it is what every export
 * has emitted, and template CSS of the form `.cs-picture img { … }` resolves against it
 * today; moving it would break that across a far larger population than this fixes. Do not
 * "tidy" the two paths into one without measuring that.
 */
function imagePayload(img, entry, picture = null) {
  const imageAttrs = {};
  // C5: fetchpriority rides the EXISTING extra-attrs channel rather than a new
  // positional shortcode argument. `{% image src, alt, class, loading, sizes,
  // attrs %}` already has six positions and two of them are optional; a seventh
  // would have to be emitted whenever the seventh was wanted, which is how a
  // shortcode call ends up with an empty `sizes` in the middle of it.
  const fetchpriority = img.getAttribute('fetchpriority') || '';
  if (fetchpriority === 'high') imageAttrs.fetchpriority = 'high';
  const style = img.getAttribute('style') || '';
  const hover = img.getAttribute('data-image-hover') || '';
  const effect = img.getAttribute('data-image-effect') || '';
  if (style) imageAttrs.style = style;
  if (img.hasAttribute('data-image-edit')) imageAttrs.dataImageEdit = true;
  if (IMAGE_HOVER_PRESETS.has(hover)) imageAttrs.dataImageHover = hover;
  if (IMAGE_EFFECT_PRESETS.has(effect)) imageAttrs.dataImageEffect = effect;

  const imgClass = img.getAttribute('class') || '';
  const pictureClass = picture ? picture.getAttribute('class') || '' : '';
  if (picture && imgClass) imageAttrs.imgClass = imgClass;

  return {
    src: entry.shortcodeSrc,
    alt: img.getAttribute('alt') || '',
    className: picture ? pictureClass : imgClass,
    loading: img.getAttribute('loading') || 'lazy',
    imageAttrs
  };
}

function placeholderComment(doc, payload) {
  return doc.createComment(`PB_IMG:${encodePayload(payload)}`);
}

function shortcodeFromPayload(payload) {
  const args = [
    shortcodeArg(payload.src),
    shortcodeArg(payload.alt),
    shortcodeArg(payload.className),
    shortcodeArg(payload.loading)
  ];
  if (payload.imageAttrs && Object.keys(payload.imageAttrs).length > 0) {
    args.push(shortcodeArg(DEFAULT_IMAGE_SIZES));
    args.push(shortcodeArg(JSON.stringify(payload.imageAttrs)));
  }
  return `{% image ${args.join(', ')} %}`;
}

/**
 * A `<picture>` that only offers FORMAT alternatives (webp/jpeg of the same
 * image) and can therefore be replaced wholesale by a generated one. A picture
 * whose sources carry `media` is ART DIRECTION — genuinely different images per
 * breakpoint — which no generator can reproduce, so it stays as authored.
 *
 * Exported so the Astro rewriter asks the same question. Two copies of this
 * test would eventually disagree, and the disagreement would either flatten an
 * art-directed picture to one image or leave a format-variation picture
 * unoptimized.
 */
export function isFormatVariationPicture(picture) {
  const sources = Array.from(picture.querySelectorAll('source'));
  if (sources.length === 0) return false;
  return sources.every((source) => !source.hasAttribute('media') && !!source.getAttribute('type'));
}

export function rewriteImagesToShortcode(html, resolvedMap) {
  const convertedSources = new Set();
  if (!html) return { html: '', count: 0, convertedSources };

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return { html, count: 0, convertedSources };

  const index = buildImageEntryIndex(resolvedMap);
  const skipImgs = new WeakSet();
  let count = 0;

  for (const picture of Array.from(root.querySelectorAll('picture'))) {
    const img = picture.querySelector('img');
    if (!img) continue;

    if (!isFormatVariationPicture(picture)) {
      skipImgs.add(img);
      continue;
    }

    if (shouldSkipImage(img)) {
      skipImgs.add(img);
      continue;
    }

    const entry = resolveImageEntry(img, index);
    if (!entry) {
      skipImgs.add(img);
      continue;
    }

    const originalSource = img.getAttribute('src') || firstSrcsetUrl(img.getAttribute('srcset'));
    if (originalSource) convertedSources.add(originalSource);
    picture.replaceWith(placeholderComment(doc, imagePayload(img, entry, picture)));
    count += 1;
  }

  for (const img of Array.from(root.querySelectorAll('img'))) {
    if (skipImgs.has(img) || shouldSkipImage(img)) continue;
    const entry = resolveImageEntry(img, index);
    if (!entry) continue;
    const originalSource = img.getAttribute('src') || firstSrcsetUrl(img.getAttribute('srcset'));
    if (originalSource) convertedSources.add(originalSource);
    img.replaceWith(placeholderComment(doc, imagePayload(img, entry)));
    count += 1;
  }

  const serialized = root.innerHTML.replace(
    /<!--PB_IMG:([A-Za-z0-9+/=]+)-->/g,
    (_match, encoded) => {
      try {
        return shortcodeFromPayload(decodePayload(encoded));
      } catch {
        return '';
      }
    }
  );

  return { html: serialized, count, convertedSources };
}
